import React from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useI18n } from '../i18n/LanguageContext';
import { getPetProductById, PET_PRODUCTS } from '../data/petProducts';
import SimilarProductsCarousel from '../components/SimilarProductsCarousel';
import siteImageManifest from '../data/siteImageManifest.generated.json';

function formatNumber(n) {
  if (typeof n !== 'number') return '';
  if (Number.isInteger(n)) return String(n);
  return String(n).replace('.', ',');
}

function formatLitersFromMl(ml) {
  const liters = ml / 1000;
  const s = Number.isInteger(liters) ? String(liters) : String(liters).replace(/0+$/g, '').replace(/\.$/, '');
  return `${s}L`;
}

const DISPLAY_VOLUME_OVERRIDES = {
  'pet-baby-330': 350,
  'pet-laptic-400': 450,
  'pet-minipocket-150': 200,
  'pet-minipicola-150': 50,
};

const PET_INDUSTRY_ORDER = [
  'water',
  'alcoholic',
  'beer',
  'church',
  'chemicals',
  'sportsdrink',
  'soap',
  'cosmetics',
  'detergent',
  'pharma',
  'fresh',
  'icetea',
  'dairy',
  'vinegar',
  'nonalcoholic',
  'syrups',
  'oil',
  'wine',
];

const PET_INDUSTRY_SET = new Set(PET_INDUSTRY_ORDER);

const INDUSTRY_ALIASES = {
  water: 'water',
  apa: 'water',
  alcoholic: 'alcoholic',
  'bauturi alcoolice': 'alcoholic',
  beer: 'beer',
  bere: 'beer',
  church: 'church',
  biserica: 'church',
  chemicals: 'chemicals',
  chimice: 'chemicals',
  sportsdrink: 'sportsdrink',
  'bauturi sport': 'sportsdrink',
  soap: 'soap',
  sapun: 'soap',
  cosmetics: 'cosmetics',
  cosmetice: 'cosmetics',
  detergent: 'detergent',
  pharma: 'pharma',
  farmaceutice: 'pharma',
  fresh: 'fresh',
  'fresh smoothies': 'fresh',
  'fresh & smoothies': 'fresh',
  icetea: 'icetea',
  'ice tea': 'icetea',
  'ice-tea': 'icetea',
  dairy: 'dairy',
  lactate: 'dairy',
  vinegar: 'vinegar',
  otet: 'vinegar',
  nonalcoholic: 'nonalcoholic',
  racoritoare: 'nonalcoholic',
  syrups: 'syrups',
  siropuri: 'syrups',
  oil: 'oil',
  ulei: 'oil',
  wine: 'wine',
  vin: 'wine',
};

const LABEL_DIMENSIONS_BY_VARIANT_ID = {
  'pet-karolacta-330': { diameter: '60.5 mm', maxHeight: '82 mm' },
  'pet-karolacta-450': { diameter: '70 mm', maxHeight: '96 mm' },
  'pet-karolacta-500': { diameter: '70 mm', maxHeight: '133 mm' },
  'pet-karolacta-900': { diameter: '82.2 mm', maxHeight: '146 mm' },
  'pet-karolacta-1500': { diameter: '96 mm', maxHeight: '120 mm' },
  'pet-lapte-patrat-2000': { diameter: '100 mm', maxHeight: '100 mm' },
  'pet-lapte-400': { diameter: '59 mm', maxHeight: '85 mm' },
  'pet-lapte-500': { diameter: '65 mm', maxHeight: '118 mm' },
  'pet-lapte-2000': { diameter: '109 mm', maxHeight: '109 mm' },
  'pet-laptic-400': { diameter: '64 mm', maxHeight: '55 mm' },
  'pet-laptic-500': { diameter: '64 mm', maxHeight: '64 mm' },
  'pet-laptic-750': { diameter: '77.5 mm', maxHeight: '84 mm' },
  'pet-paco-900': { diameter: '69 mm - 88 mm', maxHeight: '69 mm - 204 mm' },
  'pet-basilica-850': { diameter: '78 mm', maxHeight: '90 mm' },
  'pet-basilica-950': { diameter: '78 mm', maxHeight: '100 mm' },
  'pet-select-400': { diameter: '62 mm', maxHeight: '65 mm' },
  'pet-select-500': { diameter: '62.5 mm', maxHeight: '74 mm' },
  'pet-elegant-lapte-500': { diameter: '83 mm', maxHeight: '50 mm' },
  'pet-omnia-700': { diameter: '83 mm', maxHeight: '100 mm' },
  'pet-vio-1000': { diameter: '80 mm', maxHeight: '79 mm' },
  'pet-baby-150': { diameter: '62 mm', maxHeight: '24.5 mm' },
  'pet-baby-250': { diameter: '62 mm', maxHeight: '45 mm' },
  'pet-baby-300': { diameter: '62 mm', maxHeight: '70 mm' },
  'pet-baby-350': { diameter: '62 mm', maxHeight: '70 mm' },
  'pet-baby-400': { diameter: '62 mm', maxHeight: '85 mm' },
  'pet-baby-450': { diameter: '62 mm', maxHeight: '114 mm' },
  'pet-baby-500': { diameter: '62 mm', maxHeight: '110 mm' },
  'pet-baby-1000': { diameter: '77 mm', maxHeight: '124 mm' },
  'pet-crin-900': { labelSize: 'Informație pe schiță' },
  'pet-cristal-1000': { diameter: '80 mm', maxHeight: '106 mm' },
  'pet-comuna-500': { diameter: '63.5 mm', maxHeight: '100 mm' },
  'pet-comuna-cu-cruce-500': { diameter: '63.5 mm', maxHeight: '100 mm' },
  'pet-standard-900': { diameter: '80 mm', maxHeight: '104 mm' },
  'pet-standard-1000': { diameter: '80 mm', maxHeight: '125 mm' },
  'pet-standard-ulei-1000': { diameter: '80 mm', maxHeight: '116 mm' },
  'pet-otet-500': { diameter: '63 mm', maxHeight: '104 mm' },
  'pet-otet-1000': { diameter: '79.5 mm', maxHeight: '129.5 mm' },
  'pet-fresh-250': { diameter: '53 mm', maxHeight: '75 mm' },
  'pet-rotund-300': { diameter: '54 mm', maxHeight: '111 mm' },
  'pet-rotund-500': { diameter: '69.5 mm', maxHeight: '123 mm' },
  'pet-rotund-1000': { diameter: '78 mm', maxHeight: '166 mm' },
  'pet-tineret-500': { diameter: '62 mm', maxHeight: '64 mm' },
  'pet-minipocket-150': { labelSize: 'Informație pe schiță' },
  'pet-perfect-2000': { diameter: '97.5 mm', maxHeight: '108 mm' },
  'pet-plosca-2000': { labelSize: 'Informație pe schiță' },
  'pet-damigeana-5000': { diameter: '155 mm - 190 mm', maxHeight: '129 mm' },
  'pet-sapun-500': { labelSize: 'Informație pe schiță' },
  'pet-finete-250': { labelSize: 'Informație pe schiță' },
  'pet-finete-500': { labelSize: 'Informație pe schiță' },
  'pet-elegant-500': { diameter: '83 mm', maxHeight: '50 mm' },
  'pet-lebada-500': { labelSize: 'Informație pe schiță' },
  'pet-lebada-750': { labelSize: 'Informație pe schiță' },
  'pet-practic-500': { labelSize: 'Informație pe schiță' },
  'pet-balsam-nr-1-1500': { labelSize: 'Informație pe schiță' },
  'pet-balsam-nr-2-1500': { labelSize: 'Informație pe schiță' },
  'pet-flacon-200': { diameter: '50 mm', maxHeight: '92 mm' },
  'pet-miniflacon-150': { diameter: '51 mm', maxHeight: '67.5 mm' },
  'pet-picola-100': { diameter: '42.5 mm', maxHeight: '70.9 mm' },
  'pet-minipicola-50': { diameter: '37 mm', maxHeight: '38 mm' },
  'pet-minipicola-150': { diameter: '37 mm', maxHeight: '38 mm' },
  'pet-avantaj-3000': { labelSize: 'Informație pe schiță' },
  'pet-premium-5000': { labelSize: 'Informație pe schiță' },
  'pet-jumbo-10000': { diameter: '835 mm', maxHeight: '220 mm' },
};

const SHRINK_SLEEVE_EXCLUDED_IDS = new Set([
  'pet-minipocket-150',
  'pet-tineret-500',
  'pet-damigeana-5000',
  'pet-plosca-2000',
  'pet-premium-5000',
  'pet-jumbo-10000',
  'pet-otet-1000',
]);

const SKETCH_PDF_BY_VARIANT_ID = {
  'pet-baby-150': 'Baby_150ml_Younger_Karom.pdf',
  'pet-baby-1000': 'Baby_1000ml_Younger_Karom.pdf',
  'pet-baby-250': 'Baby_250ml_Younger_Karom.pdf',
  'pet-baby-300': 'Baby_300ml_Younger_Karom.pdf',
  'pet-baby-350': 'Baby_350ml_Younger_Karom.pdf',
  'pet-baby-450': 'Baby_450ml_Younger_Karom.pdf',
  'pet-baby-500': 'Baby_500ml_Younger_Karom.pdf',
  'pet-balsam-nr-2-1500': 'Balsam_II_1500ml_Younger_Karom.pdf',
  'pet-lapte-400': 'Bucuros_400ml_Younger_Karom.pdf',
  'pet-comuna-500': 'Comuna_PCO_500ml_Younger_Karom.pdf',
  'pet-comuna-cu-cruce-500': 'Comuna_cu_cruce_500ml_Younger_Karom.pdf',
  'pet-crin-900': 'Crin_1000ml_Younger_Karom.pdf',
  'pet-cristal-1000': 'Cristalin_1000ml_Younger_Karom.pdf',
  'pet-elegant-500': 'Elegant_500ml__Younger_Karom.pdf',
  'pet-finete-250': 'Finete_250ml_Younger_Karom.pdf',
  'pet-finete-500': 'Finete_500ml_Younger_Karom.pdf',
  'pet-flacon-200': 'Flacon_PCO_200ml_Younger_Karom.pdf',
  'pet-jumbo-10000': 'Jumbo_10000ml_Younger_Karom.pdf',
  'pet-karolacta-1500': 'Karolacta_1500ml_Younger_Karom.pdf',
  'pet-karolacta-330': 'Karolacta_330ml_Younger_Karom.pdf',
  'pet-karolacta-500': 'Karolacta_500ml_Younger_Karom.pdf',
  'pet-karolacta-900': 'Karolacta_900ml_Younger_Karom.pdf',
  'pet-lapte-2000': 'Lapte_2000ml_Younger_Karom.pdf',
  'pet-lapte-500': 'Lapte_500ml_Younger_Karom.pdf',
  'pet-laptic-500': 'Laptic_500ml_Younger_Karom.pdf',
  'pet-lebada-500': 'Lebada_500ml_Younger_Karom.pdf',
  'pet-miniflacon-150': 'Miniflacon_3025_150ml_Younger_Karom.pdf',
  'pet-minipicola-150': 'Minipicola_3025_50ml_Younger_Karom.pdf',
  'pet-minipocket-150': 'Minipocket_PCO_200ml_Younger_Karom.pdf',
  'pet-picola-100': 'Picola_3025_100ml_Younger_Karom.pdf',
  'pet-paco-900': 'Paco_900ml_Younger_Karom.pdf',
  'pet-plosca-2000': 'Plosca_2000ml_Younger_Karom.pdf',
  'pet-premium-5000': 'Premium_5000_Younger_Karom.pdf',
  'pet-practic-500': 'Practic_500ml_Younger_Karom.pdf',
  'pet-sapun-500': 'Sapun_500ml_Younger_Karom.pdf',
  'pet-select-500': 'Select_500ml_Younger_Karom.pdf',
  'pet-basilica-850': 'Basilica_850ml_Younger_Karom.pdf',
  'pet-basilica-950': 'Basilica_950ml_Younger_Karom.pdf',
  'pet-standard-1000': 'Standard_PCO_1000ml_Younger_Karom.pdf',
  'pet-standard-900': 'Standard_PCO_900ml_Younger_Karom.pdf',
  'pet-standard-ulei-1000': 'Standard_Uei_1000ml_Younger_Karom.pdf',
  'pet-tineret-500': 'Tineret_PCO_500ml_Younger_Karom.pdf',
};

function getSketchPdfFilename(variantId) {
  return SKETCH_PDF_BY_VARIANT_ID[String(variantId || '').toLowerCase()] || '';
}

function getDisplayVolumeMl(variant) {
  const id = variant?.id;
  if (id && Object.prototype.hasOwnProperty.call(DISPLAY_VOLUME_OVERRIDES, id)) {
    return DISPLAY_VOLUME_OVERRIDES[id];
  }
  return typeof variant?.fillVolumeMl === 'number' ? variant.fillVolumeMl : 0;
}

function getDisplayVolumeText(variant) {
  const id = String(variant?.id || '').toLowerCase();
  return `${formatNumber(getDisplayVolumeMl(variant))} ml`;
}

function getDisplayBaseNameOverride(group, variant) {
  const groupKey = String(group?.groupKey || '').toLowerCase();
  const id = String(variant?.id || '').toLowerCase();

  if (groupKey === 'balsam-nr-1') return 'Balsam Nr I';
  if (groupKey === 'balsam-nr-2') return 'Balsam Nr II';
  if (groupKey === 'elegant-lapte') return 'Elegant Lapte';
  if (groupKey === 'lapte-patrat') return 'Lapte Pătrat';
  if (groupKey === 'cristal') return 'Cristalin';
  if (groupKey === 'finete') return 'Finețe';
  if (groupKey === 'laptic') return 'Lăptic';
  if (groupKey === 'lebada') return 'Lebădă';
  if (groupKey === 'standard-ulei') return 'Standard Ulei';
  if (groupKey === 'sapun') return 'Săpun';
  if (groupKey === 'omnia') return 'Voios';
  if (groupKey === 'fresh') return 'Smoothies';
  if (id === 'pet-lapte-400') return 'Bucuros';

  return '';
}

function roundToInt(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return null;
  return Math.round(n);
}

function computeCo2(weightG, factor) {
  if (typeof weightG !== 'number' || Number.isNaN(weightG)) return null;
  return weightG * factor;
}

const CO2_FACTORS = {
  rpet30: 2.03,
  pet: 2.5,
  glass: 1.4,
};

function inferBaseNameFromVariantTitle(title) {
  if (!title) return '';
  return String(title)
    .replace(/^\s*[\d.,]+\s*ml\s+/i, '')
    .trim();
}

function toSentenceCaseName(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function getVariantTitle(t, group, variant) {
  const displayBaseOverride = getDisplayBaseNameOverride(group, variant);

  const i18nBase = (() => {
    if (!variant?.i18nKey) return '';
    const key = `${variant.i18nKey}.title`;
    const translated = t(key);
    if (translated === key) return '';
    return inferBaseNameFromVariantTitle(translated);
  })();

  const baseName = (() => {
    if (displayBaseOverride) return displayBaseOverride;

    if (group?.displayName) return group.displayName;
    if (i18nBase) return i18nBase;
    const preferred = group?.variants?.find((v) => v.i18nKey) || group?.variants?.[0] || null;
    if (preferred?.i18nKey) {
      const key = `${preferred.i18nKey}.title`;
      const translated = t(key);
      if (translated !== key) return inferBaseNameFromVariantTitle(translated);
    }
    return group?.groupKey || 'PET';
  })();

  const ml = getDisplayVolumeMl(variant);
  const liters = (() => {
    if (!ml) return '';
    const val = ml / 1000;
    if (Number.isInteger(val)) return String(val);
    return String(val).replace(/0+$/g, '').replace(/\.$/, '');
  })();
  const displayBase = displayBaseOverride ? baseName : toSentenceCaseName(baseName);
  return `${displayBase} ${liters ? `${liters}L` : ''}`.trim();
}

function getColorLabel(t, color) {
  if (!color) return '';
  const key = `products.pet.colors.${color}`;
  const translated = t(key);
  return translated === key ? String(color) : translated;
}

function joinUniqueStrings(values) {
  const out = [];
  const seen = new Set();
  for (const v of values || []) {
    const s = String(v || '').trim();
    if (!s) continue;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out.join(', ');
}

function normalizeTextValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s*&\s*/g, ' & ')
    .replace(/[^a-z0-9&\s-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function toIndustryId(value) {
  const normalized = normalizeTextValue(value);
  if (!normalized) return '';

  if (PET_INDUSTRY_SET.has(normalized)) return normalized;
  if (INDUSTRY_ALIASES[normalized]) return INDUSTRY_ALIASES[normalized];

  return '';
}

function localizeLabelDimensionValue(t, value) {
  const normalized = normalizeTextValue(value);
  if (normalized === 'informatie pe schita' || normalized === 'informatie pe schita') {
    const translated = t('products.pet.specs.labelSizeOnSketch');
    return translated === 'products.pet.specs.labelSizeOnSketch' ? String(value || '') : translated;
  }

  return String(value || '');
}

function similarityScore(a, b) {
  let score = 0;
  if (a.neckType && b.neckType && a.neckType === b.neckType) score += 3;
  if (typeof a.fillVolumeMl === 'number' && typeof b.fillVolumeMl === 'number' && a.fillVolumeMl === b.fillVolumeMl)
    score += 2;
  if (a.color && b.color && a.color === b.color) score += 1;
  return score;
}

export default function PetProduct() {
  const { t } = useI18n();
  const { productId } = useParams();
  const location = useLocation();
  const v = typeof siteImageManifest?.generatedAt === 'string' ? siteImageManifest.generatedAt : '';
  const suffix = v ? `?v=${encodeURIComponent(v)}` : '';
  const arrowIconSrc = `${process.env.PUBLIC_URL}/images/altele/arrow.png${suffix}`;

  const group = React.useMemo(() => getPetProductById(productId), [productId]);
  const variants = React.useMemo(() => group?.variants ?? [], [group]);

  const variant = React.useMemo(() => {
    if (!variants.length) return null;
    return variants.find((v) => v.id === productId) || null;
  }, [productId, variants]);

  const resolvedVariant = React.useMemo(() => {
    if (variant) return variant;
    return variants[0] || null;
  }, [variant, variants]);

  const shouldRedirectToVariant = Boolean(group && !variant && resolvedVariant);
  const redirectTo = shouldRedirectToVariant
    ? `/produse/sticle-pet/${resolvedVariant.id}${location.search || ''}`
    : '';

  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
  React.useEffect(() => setSelectedImageIndex(0), [resolvedVariant?.id]);

  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);

  const [isCo2DisclaimerOpen, setIsCo2DisclaimerOpen] = React.useState(false);
  const co2DisclaimerRef = React.useRef(null);

  const closeLightbox = React.useCallback(() => setIsLightboxOpen(false), []);
  const openLightbox = React.useCallback(() => setIsLightboxOpen(true), []);

  const closeCo2Disclaimer = React.useCallback(() => setIsCo2DisclaimerOpen(false), []);
  const toggleCo2Disclaimer = React.useCallback(() => setIsCo2DisclaimerOpen((v) => !v), []);

  const specsRef = React.useRef(null);
  const [photoTargetHeight, setPhotoTargetHeight] = React.useState(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof ResizeObserver === 'undefined') return;

    const mql = typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 901px)') : null;
    let raf = 0;
    const measure = () => {
      if (!specsRef.current) return;
      if (mql && !mql.matches) {
        setPhotoTargetHeight(null);
        return;
      }

      const rect = specsRef.current.getBoundingClientRect();
      const h = Math.round(rect.height || 0);

      if (h <= 0) {
        setPhotoTargetHeight(null);
        return;
      }

      const minHeight = 520;
      const extra = 60;
      const maxHeight = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.75) : 0;
      let desired = Math.max(minHeight, h + extra);
      if (maxHeight > 0) desired = Math.min(desired, maxHeight);

      setPhotoTargetHeight(desired);
    };

    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(measure);
    });

    if (specsRef.current) ro.observe(specsRef.current);
    measure();

    const onMqlChange = () => measure();
    if (mql) {
      if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onMqlChange);
      else if (typeof mql.addListener === 'function') mql.addListener(onMqlChange);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      if (mql) {
        if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onMqlChange);
        else if (typeof mql.removeListener === 'function') mql.removeListener(onMqlChange);
      }
    };
  }, [resolvedVariant?.id]);

  const title = React.useMemo(() => {
    if (!group || !resolvedVariant) return '';
    return getVariantTitle(t, group, resolvedVariant);
  }, [t, group, resolvedVariant]);

  const colorLabel = React.useMemo(() => {
    if (!resolvedVariant) return '';
    return getColorLabel(t, resolvedVariant.color);
  }, [t, resolvedVariant]);

  const availableColorsLabel = React.useMemo(() => {
    if (!resolvedVariant) return '';
    const ids = Array.isArray(resolvedVariant.availableColors) ? resolvedVariant.availableColors : [];
    if (!ids.length) return colorLabel;
    const labels = ids.map((id) => getColorLabel(t, id)).filter(Boolean);
    return joinUniqueStrings(labels) || colorLabel;
  }, [t, resolvedVariant, colorLabel]);

  const neckTypesLabel = React.useMemo(() => {
    if (!resolvedVariant) return '';
    if (resolvedVariant.id === 'pet-standard-ulei-1000') return '29/21';

    const ids = Array.isArray(resolvedVariant.neckTypes) ? resolvedVariant.neckTypes : [];
    if (!ids.length) {
      return resolvedVariant.neckType ? t(`products.filters.options.neckTypes.${resolvedVariant.neckType}`) : '';
    }

    const labels = ids
      .map((id) => {
        const key = `products.filters.options.neckTypes.${id}`;
        const translated = t(key);
        return translated === key ? String(id) : translated;
      })
      .filter(Boolean);

    return joinUniqueStrings(labels);
  }, [t, resolvedVariant]);

  const industriesLabel = React.useMemo(() => {
    if (!resolvedVariant) return '';
    const industries = Array.isArray(resolvedVariant.industries) ? resolvedVariant.industries : [];
    const labels = industries.map((industry) => {
      const id = toIndustryId(industry);
      if (!id) return String(industry || '');

      const key = `products.filters.options.segments.${id}`;
      const translated = t(key);
      return translated === key ? String(industry || '') : translated;
    });
    return joinUniqueStrings(labels);
  }, [t, resolvedVariant]);

  const materialLabel = React.useMemo(() => {
    if (!resolvedVariant?.material) return '-';
    return resolvedVariant.material === 'PET / rPET' ? 'PET / 30% rPET' : resolvedVariant.material;
  }, [resolvedVariant]);

  const labelDimensions = React.useMemo(() => {
    if (!resolvedVariant?.id) return null;
    const dimensions = LABEL_DIMENSIONS_BY_VARIANT_ID[String(resolvedVariant.id)] || null;
    if (!dimensions) return null;

    return {
      ...dimensions,
      labelSize: dimensions.labelSize ? localizeLabelDimensionValue(t, dimensions.labelSize) : dimensions.labelSize,
    };
  }, [t, resolvedVariant]);

  const supportsShrinkSleeve = React.useMemo(() => {
    if (!resolvedVariant?.id) return false;
    return !SHRINK_SLEEVE_EXCLUDED_IDS.has(String(resolvedVariant.id));
  }, [resolvedVariant]);

  const co2Bars = React.useMemo(() => {
    if (!resolvedVariant) return [];

    const petWeightG =
      typeof resolvedVariant.petWeightG === 'number'
        ? resolvedVariant.petWeightG
        : typeof resolvedVariant.weightG === 'number'
          ? resolvedVariant.weightG
          : null;
    const glassWeightG = typeof resolvedVariant.glassWeightG === 'number' ? resolvedVariant.glassWeightG : null;

    const rpet30 = computeCo2(petWeightG, CO2_FACTORS.rpet30);
    const pet = computeCo2(petWeightG, CO2_FACTORS.pet);
    const glass = computeCo2(glassWeightG, CO2_FACTORS.glass);

    /** @type {Array<{key: string, label: string, value: number|null}>} */
    const bars = [
      { key: 'rpet', label: t('products.pet.co2.labels.rpet'), value: rpet30 },
      { key: 'pet', label: t('products.pet.co2.labels.pet'), value: pet },
      { key: 'glass', label: t('products.pet.co2.labels.glass'), value: glass },
    ];

    const numeric = bars.map((b) => (typeof b.value === 'number' ? b.value : 0));
    const max = Math.max(...numeric, 0);

    return bars.map((b) => {
      const v = typeof b.value === 'number' ? b.value : null;
      const pct = max > 0 && typeof v === 'number' ? (v / max) * 100 : 0;
      const display = v === null ? '-' : `${roundToInt(v) ?? '-'}g`;
      return { ...b, pct: Math.max(0, Math.min(100, pct)), display };
    });
  }, [t, resolvedVariant]);

  const similarItems = React.useMemo(() => {
    if (!resolvedVariant) return [];

    /** @type {Array<{group: any, variant: any, score: number}>} */
    const candidates = [];

    for (const g of PET_PRODUCTS) {
      for (const v of g.variants) {
        if (!v || v.id === resolvedVariant.id) continue;
        const score = similarityScore(resolvedVariant, v);
        if (score <= 0) continue;
        candidates.push({ group: g, variant: v, score });
      }
    }

    candidates.sort((x, y) => {
      if (y.score !== x.score) return y.score - x.score;
      const dx = Math.abs((x.variant.fillVolumeMl || 0) - (resolvedVariant.fillVolumeMl || 0));
      const dy = Math.abs((y.variant.fillVolumeMl || 0) - (resolvedVariant.fillVolumeMl || 0));
      if (dx !== dy) return dx - dy;
      return String(x.variant.id).localeCompare(String(y.variant.id));
    });

    const top = candidates.slice(0, 16);
    return top.map(({ group: g, variant: v }) => {
      const hasPhotos = Array.isArray(v.images) && v.images.length;
      const imgSrc = hasPhotos ? v.images[0] : `${process.env.PUBLIC_URL}/brand/yk-mark.svg`;
      return {
        to: `/produse/sticle-pet/${v.id}`,
        title: getVariantTitle(t, g, v),
        imgSrc,
        isPhoto: Boolean(hasPhotos),
      };
    });
  }, [t, resolvedVariant]);

  const images = Array.isArray(resolvedVariant?.images) ? resolvedVariant.images : [];
  const imagesCount = images.length;
  const isPhoto = imagesCount > 0;
  const clampedImageIndex = imagesCount ? Math.min(selectedImageIndex, imagesCount - 1) : 0;
  const imgSrc = isPhoto ? images[clampedImageIndex] : `${process.env.PUBLIC_URL}/brand/yk-mark.svg`;

  const canCyclePhotos = imagesCount > 1;
  const prevLabel = t('common.prev') === 'common.prev' ? 'Previous photo' : t('common.prev');
  const nextLabel = t('common.next') === 'common.next' ? 'Next photo' : t('common.next');

  const showPrevPhoto = React.useCallback(() => {
    if (!canCyclePhotos) return;
    setSelectedImageIndex((idx) => (idx - 1 + imagesCount) % imagesCount);
  }, [canCyclePhotos, imagesCount]);

  const showNextPhoto = React.useCallback(() => {
    if (!canCyclePhotos) return;
    setSelectedImageIndex((idx) => (idx + 1) % imagesCount);
  }, [canCyclePhotos, imagesCount]);

  React.useEffect(() => {
    if (!isLightboxOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLightbox();
        return;
      }
      if (canCyclePhotos && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        if (e.key === 'ArrowLeft') showPrevPhoto();
        else showNextPhoto();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isLightboxOpen, canCyclePhotos, closeLightbox, showPrevPhoto, showNextPhoto]);

  React.useEffect(() => {
    if (!isCo2DisclaimerOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeCo2Disclaimer();
      }
    };

    const onPointerDown = (e) => {
      const root = co2DisclaimerRef.current;
      if (!root) return;
      if (root.contains(e.target)) return;
      closeCo2Disclaimer();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isCo2DisclaimerOpen, closeCo2Disclaimer]);

  if (!group) return <Navigate to="/produse/sticle-pet" replace />;
  if (!resolvedVariant) return <Navigate to="/produse/sticle-pet" replace />;
  if (shouldRedirectToVariant) return <Navigate to={redirectTo} replace />;

  const params = new URLSearchParams();
  params.set('category', 'sticle-pet');
  params.set('productId', resolvedVariant.id);
  params.set('productTitle', title);
  params.set('fillVolumeMl', String(getDisplayVolumeMl(resolvedVariant)));
  if (typeof resolvedVariant.weightG === 'number') params.set('weightG', String(resolvedVariant.weightG));
  if (resolvedVariant.material) params.set('material', resolvedVariant.material);
  if (Array.isArray(resolvedVariant.neckTypes) && resolvedVariant.neckTypes.length)
    params.set('neckTypes', resolvedVariant.neckTypes.join(','));
  if (neckTypesLabel) params.set('neckType', neckTypesLabel);
  if (Array.isArray(resolvedVariant.shapes) && resolvedVariant.shapes.length) params.set('shapes', resolvedVariant.shapes.join(','));
  if (resolvedVariant.shape) params.set('shape', t(`products.filters.options.shapes.${resolvedVariant.shape}`));
  const contactUrl = `/contact?${params.toString()}`;

  const hasTechSheet = resolvedVariant.hasTechSheet !== false;
  const techSheetUrl = `${process.env.PUBLIC_URL}/tech-sheets/pet/${resolvedVariant.id}.pdf`;
  const sketchPdfFilename = getSketchPdfFilename(resolvedVariant.id);
  const sketchPdfUrl = sketchPdfFilename
    ? `${process.env.PUBLIC_URL}/images/schite/${encodeURIComponent(sketchPdfFilename)}${suffix}`
    : '';

  return (
    <section className="yk-page yk-pageProductDetail">
      <h1 className="yk-productDetailTitle">{title}</h1>

      <div className="yk-productDetailLayout">
        <div className="yk-productDetailImage">
          <div
            className={
              isPhoto
                ? canCyclePhotos
                  ? 'yk-productDetailPhotoWrap is-photo has-arrows'
                  : 'yk-productDetailPhotoWrap is-photo'
                : 'yk-productDetailPhotoWrap'
            }
            style={photoTargetHeight ? { height: photoTargetHeight } : undefined}
          >
            {canCyclePhotos ? (
              <button
                type="button"
                className="yk-productDetailPhotoArrow is-left"
                aria-label={prevLabel}
                onClick={showPrevPhoto}
              >
                ‹
              </button>
            ) : null}

            <img
              className={isPhoto ? 'yk-productDetailImg is-photo' : 'yk-productDetailImg'}
              src={imgSrc}
              alt={isPhoto ? title : ''}
              aria-hidden={isPhoto ? undefined : 'true'}
              loading={isPhoto ? 'eager' : 'lazy'}
              decoding="async"
              role={isPhoto ? 'button' : undefined}
              tabIndex={isPhoto ? 0 : undefined}
              onClick={isPhoto ? openLightbox : undefined}
              onKeyDown={
                isPhoto
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openLightbox();
                      }
                    }
                  : undefined
              }
              style={photoTargetHeight && isPhoto ? { height: '100%', width: 'auto' } : undefined}
            />

            {canCyclePhotos ? (
              <button
                type="button"
                className="yk-productDetailPhotoArrow is-right"
                aria-label={nextLabel}
                onClick={showNextPhoto}
              >
                ›
              </button>
            ) : null}
          </div>

          {group.variants.length > 1 ? (
            <div className="yk-volumeRow" role="navigation" aria-label={t('products.pet.specs.fillVolume')}>
              {group.variants.map((v) => (
                <Link
                  key={v.id}
                  className={v.id === resolvedVariant.id ? 'yk-btn yk-btnGhost is-active' : 'yk-btn yk-btnGhost'}
                  to={`/produse/sticle-pet/${v.id}`}
                >
                  {formatLitersFromMl(getDisplayVolumeMl(v))}
                </Link>
              ))}
            </div>
          ) : null}

          {/* Thumbnail strip removed; use arrows over the main photo instead */}
        </div>

        <aside ref={specsRef} className="yk-productDetailSpecs yk-card">
          <div className="yk-specRow">
            <div className="yk-specLabel">{t('products.pet.specs.fillVolume')}</div>
            <div className="yk-specValue">{getDisplayVolumeText(resolvedVariant)}</div>
          </div>
          <div className="yk-specRow">
            <div className="yk-specLabel">{t('products.pet.specs.material')}</div>
            <div className="yk-specValue">{materialLabel}</div>
          </div>
          <div className="yk-specRow">
            <div className="yk-specLabel">{t('products.pet.specs.color')}</div>
            <div className="yk-specValue">{availableColorsLabel || '-'}</div>
          </div>
          <div className="yk-specRow">
            <div className="yk-specLabel">{t('products.pet.specs.neckType')}</div>
            <div className="yk-specValue">{neckTypesLabel || '-'}</div>
          </div>
          <div className="yk-specRow">
            <div className="yk-specLabel">{t('products.pet.specs.shape')}</div>
            <div className="yk-specValue">
              {resolvedVariant.shape ? t(`products.filters.options.shapes.${resolvedVariant.shape}`) : '-'}
            </div>
          </div>

          {labelDimensions?.labelSize ? (
            <div className="yk-specRow">
              <div className="yk-specLabel">{t('products.pet.specs.labelSize')}</div>
              <div className="yk-specValue">{labelDimensions.labelSize}</div>
            </div>
          ) : (
            <>
              {labelDimensions?.diameter ? (
                <div className="yk-specRow">
                  <div className="yk-specLabel">{t('products.pet.specs.diameter')}</div>
                  <div className="yk-specValue">{labelDimensions.diameter}</div>
                </div>
              ) : null}

              {labelDimensions?.maxHeight ? (
                <div className="yk-specRow">
                  <div className="yk-specLabel">{t('products.pet.specs.maxRecommendedLabelHeight')}</div>
                  <div className="yk-specValue">{labelDimensions.maxHeight}</div>
                </div>
              ) : null}
            </>
          )}

          {supportsShrinkSleeve ? (
            <div className="yk-specRow">
              <div className="yk-specLabel">{t('products.pet.specs.shrinkSleeve')}</div>
              <div className="yk-specValue">{t('products.pet.specs.shrinkSleeveCompatible')}</div>
            </div>
          ) : null}

          <div className="yk-specRow">
            <div className="yk-specLabel">{t('products.pet.specs.proposedIndustry')}</div>
            <div className="yk-specValue">{industriesLabel || '-'}</div>
          </div>

          {sketchPdfUrl ? (
            <div className="yk-ctaRow" style={{ marginTop: 14, marginBottom: 8 }}>
              <a
                className="yk-btn yk-btnPrimary yk-btnWithIcon"
                href={sketchPdfUrl}
                download={sketchPdfFilename}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="yk-downloadIcon"
                  src={arrowIconSrc}
                  alt=""
                  aria-hidden="true"
                />
                <span>{t('products.pet.downloadSketch')}</span>
              </a>
            </div>
          ) : null}

          <div className="yk-ctaRow" style={{ marginTop: 14 }}>
            <Link className="yk-btn yk-btnPrimary" to={contactUrl}>
              {t('products.pet.sendRequest')}
            </Link>

            {hasTechSheet ? (
              <a
                className="yk-btn yk-btnGhost"
                href={techSheetUrl}
                download={`${variant.id}.pdf`}
                target="_blank"
                rel="noreferrer"
              >
                {t('products.pet.downloadTechSheet')}
              </a>
            ) : null}
          </div>
        </aside>
      </div>

      {isLightboxOpen && isPhoto ? (
        <div className="yk-lightboxOverlay" role="dialog" aria-modal="true" aria-label={title} onClick={closeLightbox}>
          <div className="yk-lightbox" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="yk-lightboxClose" aria-label="Close" onClick={closeLightbox}>
              ×
            </button>

            <img className="yk-lightboxImg" src={imgSrc} alt={title} />

            {canCyclePhotos ? (
              <div className="yk-lightboxNav" role="group" aria-label="Photo navigation">
                <button type="button" className="yk-lightboxArrow" aria-label={prevLabel} onClick={showPrevPhoto}>
                  ‹
                </button>
                <button type="button" className="yk-lightboxArrow" aria-label={nextLabel} onClick={showNextPhoto}>
                  ›
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {co2Bars.length ? (
        <div className="yk-card">
          <div className="yk-co2Header">
            <h2 className="yk-h2">{t('products.pet.co2.title')}</h2>
            <div className="yk-co2Disclaimer" ref={co2DisclaimerRef}>
              <button
                type="button"
                className={isCo2DisclaimerOpen ? 'yk-infoBtn is-open' : 'yk-infoBtn'}
                aria-label="Info"
                aria-expanded={isCo2DisclaimerOpen}
                onClick={toggleCo2Disclaimer}
              >
                i
              </button>
              {isCo2DisclaimerOpen ? (
                <div className="yk-popover" role="dialog" aria-modal="false">
                  <div className="yk-popoverText">{t('products.pet.co2.disclaimer')}</div>
                </div>
              ) : null}
            </div>
          </div>
          <div className="yk-co2Chart" role="group" aria-label={t('products.pet.co2.title')}>
            {co2Bars.map((b) => (
              <div key={b.key} className="yk-co2Row">
                <div className="yk-co2Label">{b.label}</div>
                <div className="yk-co2BarRow" aria-label={`${b.label}: ${b.display}`}>
                  <div className="yk-co2Pill" aria-hidden="true">
                    {b.display}
                  </div>
                  <div className="yk-co2Track" aria-hidden="true">
                    <div className="yk-co2Fill" style={{ width: `${b.pct}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <SimilarProductsCarousel title={t('products.pet.similarTitle')} items={similarItems} />
    </section>
  );
}
